<script setup lang="ts">
import type { IconValues } from '@/models/icons.enum';

export interface Props {
  name: IconValues;
}

defineProps<Props>();
</script>

<template>
  <i :class="name" v-bind="$attrs"></i>
</template>

<style scoped lang="scss">
i {
  display: block;
  width: 24px;
  height: 24px;

  &.account {
    @include icon-image('@/assets/img/icons/account.svg', var(--primary-text-color));
  }

  &.addition-sign {
    @include icon-image('@/assets/img/addition-sign.svg', var(--secondary-text-color));
  }

  &.arrow-left {
    @include icon-image('@/assets/img/icons/arrow-right.svg', var(--primary-text-color));
    transform: rotate(180deg);
  }

  &.arrow-right {
    @include icon-image('@/assets/img/icons/arrow-right.svg', var(--secondary-text-color));
  }

  &.arrow-down {
    @include icon-image('@/assets/img/ico-arrow-down.svg', var(--primary-text-color));
  }

  &.bell {
    @include icon-image('@/assets/img/icons/bell.svg', var(--primary-text-color));
  }

  &.bell-marked {
    @include icon-image('@/assets/img/icons/bell-marked.svg', var(--primary-text-color));
  }

  &.black-arrow-right {
    @include icon-image('@/assets/img/icons/arrow-right.svg', var(--primary-text-color));
  }

  &.blue-info {
    @include icon-image('@/assets/img/ico-info.svg', var(--primary-text-color));
  }

  &.black-info {
    @include icon-image('@/assets/img/info-icon.svg');
  }

  &.transparent-info-icon {
    @include icon-image('@/assets/img/transparent-info-icon.svg');
  }

  &.black-logo {
    @include icon-image('@/assets/img/black-logo.svg', var(--primary-text-color));
  }

  &.calendar {
    @include icon-image('@/assets/img/calendar.svg', var(--secondary-text-color));
  }

  &.chevron-down {
    width: 12px;
    height: 12px;
    @include icon-image('@/assets/img/ico-chevron-down.svg', var(--primary-text-color));
  }

  &.chevron-right {
    @include icon-image('@/assets/img/chevron-right.svg', var(--secondary-text-color));
    width: 12px;
    height: 12px;
  }

  &.chevron-left {
    @include icon-image('@/assets/img/icons/black_chevron_left.svg', var(--primary-text-color));
    width: 12px;
    height: 12px;
  }

  &.copy {
    @include icon-image('@/assets/img/ico-copy.svg', var(--primary-text-color));
  }

  &.dotted-circle {
    @include icon-image('@/assets/img/ico-dotted-circle.svg', var(--primary-text-color));
  }

  &.download {
    @include icon-image('@/assets/img/ico-download.svg', var(--primary-text-color));
  }

  &.edit {
    @include icon-image('@/assets/img/icons/edit.svg', var(--primary-text-color));
  }

  &.edit-white {
    @include icon-image('@/assets/img/icons/edit.svg', var(--secondary-text-color));
  }

  &.error {
    @include icon-image('@/assets/img/ico-error.svg');
  }

  &.eye-open {
    @include icon-image('@/assets/img/eye-open.svg', var(--primary-text-color));
  }

  &.eye-closed {
    @include icon-image('@/assets/img/eye-closed.svg', var(--primary-text-color));
  }

  &.gear {
    @include icon-image('@/assets/img/icons/gear.svg', var(--primary-text-color));
  }

  &.green-glitter {
    @include icon-image('@/assets/img/ico-glitter.svg', var(--primary-text-color));
  }

  &.question-mark {
    @include icon-image('@/assets/img/icons/question-mark.svg', var(--primary-text-color));
  }

  &.loading {
    @include icon-image('@/assets/img/cloud-loader.webp', var(--primary-text-color));
  }

  &.circle-loading {
    transform: rotate(0deg);
    animation: spin 1s linear infinite;
    @include icon-image('@/assets/img/circle-loading.svg', var(--primary-text-color));
  }

  &.search {
    @include icon-image('@/assets/img/ico-search.svg', var(--primary-text-color));
  }

  &.success {
    @include icon-image('@/assets/img/ico-success.svg');
  }

  &.subtract-sign {
    width: 18px;
    background: var(--secondary-text-color);
    height: 1px;
  }

  &.swiss-flag {
    @include icon-image('@/assets/img/swiss-flag.svg');
  }

  &.trash {
    @include icon-image('@/assets/img/icons/trash.svg', var(--primary-text-color));
  }

  &.triangle-warning {
    @include icon-image('@/assets/img/ico-triangle-warning.svg');
  }

  &.download-sign {
    @include icon-image('@/assets/img/addition-sign.svg', var(--secondary-text-color));
  }

  &.download-arrow-sign {
    @include icon-image('@/assets/img/ico-download-arrow.svg', var(--secondary-text-color));
  }

  &.play {
    @include icon-image('@/assets/img/icons/play.svg', var(--primary-text-color));
  }

  &.plus {
    @include icon-image('@/assets/img/icons/plus.svg');
  }

  &.recommended {
    @include icon-image('@/assets/img/icons/recommended.svg');
  }

  &.info {
    @include icon-image('@/assets/img/total-items-tooltip.svg', var(--primary-text-color));
  }

  &.info-circle {
    @include icon-image('@/assets/img/icons/info-circle.svg');
  }

  &.xlsx-file {
    @include icon-image('@/assets/img/ico-xlsx.svg', var(--primary-text-color));
  }

  &.x-close {
    @include icon-image('@/assets/img/addition-sign.svg', var(--primary-text-color));
    transform: rotate(45deg);
  }

  &.required {
    display: inline-block;
    width: 4px;
    height: 4px;
    background-color: red;
    border-radius: 50%;
    margin-bottom: 1px;
  }

  &.white-delete {
    @include icon-image('@/assets/img/icons/trash.svg', var(--secondary-text-color));
  }

  &.white-logo {
    @include icon-image('@/assets/img/white-logo.svg');
  }

  &.volume-muted {
    @include icon-image('@/assets/img/icons/volume-muted.svg');
  }

  &.volume-on {
    @include icon-image('@/assets/img/icons/volume-on.svg');
  }

  &.warning {
    @include icon-image('@/assets/img/ico-warning.svg');
  }

  &.upload {
    @include icon-image('@/assets/img/ico-upload.svg', var(--primary-text-color));
  }

  &.cross {
    @include icon-image('@/assets/img/ico-cross.svg', var(--primary-text-color));
  }

  &.filled-error {
    @include icon-image('@/assets/img/ico-cross.svg', var(--primary-text-color));
  }
}

input.hidden + i.password-icon {
  top: -18px;
}
</style>
